import { SegmentType } from '@ve-pro/enums/segment'
import { useTrackStore } from '@ve-pro/store'
import {
  addSegment,
  insertAfterTrack,
  insertBeforeTrack,
  removeSegment,
  removeTrackIfEmpty,
  removeTransitionIfVideo,
} from '@ve-pro/store/tracks/tracks-actions'
import type { SegmentCollection, Track } from '@ve-pro/types/track.type'
import { getTrackTypeBySegmentType } from './track-height'
import type { RelativePosition } from './track-insert-position'
import { showInsertError } from './track-message'
import { t } from '@ve-pro/ve/lang'
import { ProjectMode } from '@ve-pro/enums/project'
import { isSegmentFragmentChild } from '@ve-pro/store/tracks/segment/segment-fragment'

// 轨道最大数量的开关
let TRACK_MAX_SWITCH = true

/** 设置轨道最大数量无限制  */
export function setTrackMaxUnlimited(): () => void {
  TRACK_MAX_SWITCH = false

  // 重置最大数量
  return function resetTrackMax() {
    TRACK_MAX_SWITCH = true
  }
}

/**
 *
 * @param track 插入相对轨道
 * @param position 插入相对轨道的插入的位置
 * @param segment 插入的元件
 * @returns 是否可创建可插入的轨道
 */
export function expectInsertTrack(
  track: Track,
  position: RelativePosition,
  willBeInsertedSegments: SegmentCollection | SegmentCollection[], // 需要保证所有元件来自同一轨道
  options?: { removeTrackIfEmpty?: boolean },
): boolean {
  const { removeTrackIfEmpty: _removeTrackIfEmpty = true } = options || {}
  const _willBeInsertedSegments = Array.isArray(willBeInsertedSegments)
    ? willBeInsertedSegments
    : [willBeInsertedSegments]

  // 检查所有元件是否来自同一轨道
  if (
    _willBeInsertedSegments.length === 0 ||
    _willBeInsertedSegments
      .map((s) => s.track_id)
      .some((id) => id !== _willBeInsertedSegments[0]!.track_id)
  ) {
    return false
  }

  const trackStore = useTrackStore()
  const segment = _willBeInsertedSegments[0]!
  const tracks = trackStore.getCurrentTracksByMode(segment.stage)
  const targetSegmentTrack = tracks.find((t) => t.id === segment.track_id)

  const trackType = getTrackTypeBySegmentType(segment.type)
  // 验证是否超出指定轨道数量
  const TRACK_MAX = Infinity // 10

  // 轨道不存在 或者 元件所处轨道的即将被删除的情况下
  const canIgnore =
    !targetSegmentTrack ||
    targetSegmentTrack.segments.filter(
      (item: SegmentCollection) =>
        !_willBeInsertedSegments.some((s) => s.id === item.id),
    ).length === 0

  const canInsert =
    !TRACK_MAX_SWITCH ||
    tracks.filter((t) => {
      const isSomeType = t.type === trackType
      return isSomeType && (!canIgnore || t.id !== targetSegmentTrack?.id)
    }).length <=
      TRACK_MAX - 1

  if (canInsert) {
    let newTrack: Track | null = null
    // 创建轨道
    if (position === 'top') {
      newTrack = insertAfterTrack(
        getTrackTypeBySegmentType(segment.type),
        track,
      )
    } else if (position === 'bottom') {
      newTrack = insertBeforeTrack(
        getTrackTypeBySegmentType(segment.type),
        track,
      )
    }

    if (newTrack && isSegmentFragmentChild(segment)) {
      newTrack.fragment_segment_id = segment.fragment_segment_id
    }

    _willBeInsertedSegments.forEach((segment) => {
      removeTransitionIfVideo(segment)
      // 从上一个轨道移除
      removeSegment(segment, false)
      if (newTrack) {
        // 插入新创建的轨道
        addSegment(segment, newTrack)
      }
      if (_removeTrackIfEmpty && targetSegmentTrack) {
        removeTrackIfEmpty(targetSegmentTrack)
      }
    })

    return true
  }
  return false
}
export function createTrackAndInsertSegment(
  track: Track,
  position: RelativePosition,
  segments: SegmentCollection | SegmentCollection[],
  options?: { removeTrackIfEmpty?: boolean },
): boolean {
  const { removeTrackIfEmpty: _removeTrackIfEmpty = true } = options || {}
  const inserted = expectInsertTrack(track, position, segments, {
    removeTrackIfEmpty: _removeTrackIfEmpty,
  })

  if (!inserted) {
    const texts: Record<SegmentType, string> = {
      [SegmentType.Audio]: t('audio'),
      [SegmentType.Video]: t('video'),
      [SegmentType.Text]: t('text'),
      [SegmentType.Sticker]: t('sticker'),
      [SegmentType.Filter]: t('filter'),
      [SegmentType.Transition]: t('transition'),
      [SegmentType.Fragment]: t('fragment.label'),
      [SegmentType.SubtitleGroup]: t('subtitle'),
    }

    const segment = Array.isArray(segments) ? segments[0]! : segments

    const isSubtitle =
      segment.type === SegmentType.Text &&
      segment.stage === ProjectMode.Subtitle
    const text = isSubtitle ? t('subtitle') : texts[segment.type]
    showInsertError(t('track.full', { segment: text }))
  }

  return inserted
}
